import type { Dealer, Department, HoursOfOperation } from '../typings/main';

export interface ProcessedDay {
  day: string;
  openTime: string;
  closeTime: string;
  closedIndicator: boolean;
}

export interface ProcessedDepartment {
  description: string;
  departmentCode: string;
  days: ProcessedDay[];
}

export interface ProcessedDealerHours {
  dealerName: string;
  departments: ProcessedDepartment[];
  availableDepartments: Array<{
    code: string;
    description: string;
  }>;
}

const DAYS_ORDER_SUNDAY_FIRST_EN = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const DAYS_ORDER_MONDAY_FIRST_EN = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const DAYS_ORDER_SUNDAY_FIRST_FR = [
  'Dimanche',
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
];
const DAYS_ORDER_MONDAY_FIRST_FR = [
  'Lundi',
  'Mardi',
  'Mercredi',
  'Jeudi',
  'Vendredi',
  'Samedi',
  'Dimanche',
];

const DAY_NAME_MAP: Record<string, Record<string, string>> = {
  en: {
    Sunday: 'Sunday',
    Monday: 'Monday',
    Tuesday: 'Tuesday',
    Wednesday: 'Wednesday',
    Thursday: 'Thursday',
    Friday: 'Friday',
    Saturday: 'Saturday',
  },
  fr: {
    Sunday: 'Dimanche',
    Monday: 'Lundi',
    Tuesday: 'Mardi',
    Wednesday: 'Mercredi',
    Thursday: 'Jeudi',
    Friday: 'Vendredi',
    Saturday: 'Samedi',
  },
};

export function convertTo12HourFormat(time24: string, locale: string = 'en'): string {
  const [hours, minutes] = time24.split(':').map(Number);

  if (locale === 'fr') {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
}

export function getDayOrder(sundayFirst: boolean, locale: string = 'en'): string[] {
  const isFrench = locale === 'fr';

  if (isFrench) {
    return sundayFirst ? DAYS_ORDER_SUNDAY_FIRST_FR : DAYS_ORDER_MONDAY_FIRST_FR;
  }

  return sundayFirst ? DAYS_ORDER_SUNDAY_FIRST_EN : DAYS_ORDER_MONDAY_FIRST_EN;
}

export function translateDayName(dayName: string, locale: string = 'en'): string {
  const isFrench = locale === 'fr';
  if (!isFrench) return dayName;

  return DAY_NAME_MAP.fr[dayName] || dayName;
}

export function groupConsecutiveDays(
  days: string[],
  dayOrder: string[],
): string[][] {
  if (days.length === 0) return [];
  if (days.length === 1) return [days];

  const dayIndices = days
    .map((day) => ({ day, index: dayOrder.indexOf(day) }))
    .sort((a, b) => a.index - b.index);

  const groups: string[][] = [];
  let currentGroup = [dayIndices[0].day];

  for (let i = 1; i < dayIndices.length; i++) {
    const currentIndex = dayIndices[i].index;
    const previousIndex = dayIndices[i - 1].index;

    if (currentIndex === previousIndex + 1) {
      currentGroup.push(dayIndices[i].day);
    } else {
      groups.push([...currentGroup]);
      currentGroup = [dayIndices[i].day];
    }
  }

  groups.push([...currentGroup]);

  return groups;
}

export function formatDayRange(dayGroup: string[]): string {
  if (dayGroup.length === 1) {
    return dayGroup[0];
  }
  return `${dayGroup[0]} - ${dayGroup[dayGroup.length - 1]}`;
}

export function processDepartmentHours(
  departments: Department[],
  sundayFirst: boolean = false,
  locale: string = 'en',
): ProcessedDepartment[] {
  const dayOrder = getDayOrder(sundayFirst, locale);

  const processedDepartments = departments
    .filter((dept) => dept.hoursOfOperation && dept.hoursOfOperation.length > 0)
    .map((dept) => {
      const dayScheduleMap = new Map<
        string,
        { openTime: string; closeTime: string; isClosed: boolean }
      >();

      dayOrder.forEach((day) => {
        dayScheduleMap.set(day, {
          openTime: '',
          closeTime: '',
          isClosed: true,
        });
      });

      dept.hoursOfOperation.forEach((day: HoursOfOperation) => {
        const isClosed = day.closedIndicator === 'Y';
        const openTime =
          isClosed || !day.hours || day.hours.length === 0
            ? ''
            : day.hours[0].openTime;
        const closeTime =
          isClosed || !day.hours || day.hours.length === 0
            ? ''
            : day.hours[0].closeTime;

        // Translate the day name from English (API response) to the target locale
        const translatedDayName = translateDayName(day.name, locale);
        dayScheduleMap.set(translatedDayName, { openTime, closeTime, isClosed });
      });

      const scheduleGroups = new Map<string, string[]>();

      dayScheduleMap.forEach((schedule, dayName) => {
        const scheduleKey = `${schedule.openTime}|${schedule.closeTime}|${schedule.isClosed}`;

        if (!scheduleGroups.has(scheduleKey)) {
          scheduleGroups.set(scheduleKey, []);
        }
        scheduleGroups.get(scheduleKey)!.push(dayName);
      });

      const processedDays: ProcessedDay[] = [];

      scheduleGroups.forEach((daysWithSameSchedule, scheduleKey) => {
        const [openTime, closeTime, isClosedStr] = scheduleKey.split('|');
        const isClosed = isClosedStr === 'true';

        const consecutiveGroups = groupConsecutiveDays(
          daysWithSameSchedule,
          dayOrder,
        );

        consecutiveGroups.forEach((dayGroup) => {
          processedDays.push({
            day: formatDayRange(dayGroup),
            openTime: isClosed ? '' : convertTo12HourFormat(openTime, locale),
            closeTime: isClosed ? '' : convertTo12HourFormat(closeTime, locale),
            closedIndicator: isClosed,
          });
        });
      });

      processedDays.sort((a, b) => {
        const getFirstDay = (dayStr: string) => dayStr.split(' - ')[0];
        const firstDayA = getFirstDay(a.day);
        const firstDayB = getFirstDay(b.day);
        return dayOrder.indexOf(firstDayA) - dayOrder.indexOf(firstDayB);
      });

      return {
        description: dept.description,
        departmentCode: dept.departmentCode,
        days: processedDays,
      };
    });

  return processedDepartments.sort((a, b) => {
    if (a.description.toLowerCase() === 'service') return -1;
    if (b.description.toLowerCase() === 'service') return 1;
    return a.description.localeCompare(b.description);
  });
}

export function processDealerHours(
  dealerData: Dealer | undefined,
  sundayFirst: boolean = false,
  locale: string = 'en',
): ProcessedDealerHours | null {
  if (
    !dealerData ||
    !dealerData.departments ||
    dealerData.departments.length === 0
  ) {
    return null;
  }

  const processedDepartments = processDepartmentHours(
    dealerData.departments,
    sundayFirst,
    locale,
  );

  const availableDepartments = processedDepartments.map((dept) => {
    const originalDept = dealerData.departments.find(
      (original) => original.description === dept.description,
    );
    return {
      code: originalDept?.departmentCode || '',
      description: dept.description,
    };
  });

  return {
    dealerName: dealerData.name || 'Unknown Dealer',
    departments: processedDepartments,
    availableDepartments,
  };
}

export function getDepartmentSchedule(
  processedDealer: ProcessedDealerHours | null,
  departmentCode: string,
): ProcessedDepartment | null {
  if (!processedDealer || !departmentCode) {
    return null;
  }

  return (
    processedDealer.departments.find(
      (dept) => dept.departmentCode === departmentCode,
    ) || null
  );
}

export function formatTimeRange(
  openTime: string,
  closeTime: string,
  isClosed: boolean,
  locale: string = 'en',
): string {
  if (isClosed) {
    return locale === 'fr' ? 'Fermé' : 'Closed';
  }
  return `${openTime} - ${closeTime}`;
}
